import { useCallback, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { AppShell } from '@/components/AppShell/AppShell';
import { useToast } from '@/components/ToastProvider';
import { applyPredefinedFreeVoiceFallback, useSessionStore } from '@/stores/session';
import { useVideoStore } from '@/stores/video';
import { snapshotExportInput } from '@/lib/exportSnapshot';
import { isStsMode } from '@/lib/assetMode';
import { stsAssetToBlocks } from '@/lib/stsToBlocks';
import {
  shouldAutoUploadCloudVideo,
  submitAssetUpload,
} from '@/lib/uploadPipeline';
import { videoInputSize } from '@/lib/api';
import { cancelAbortedUpload } from '@/lib/cancelUpload';
import { ensureVideoDurations } from '@/lib/probeVideoDuration';
import { getPrimaryVoiceId } from '@/lib/voiceMigration';
import { withSearch } from '@/lib/nav';
import { useLeaveGuard } from '@/hooks/useLeaveGuard';
import styles from './UploadSummary.module.css';
import { scriptAttrs } from '@/lib/scriptDetect';
import { buildAssetFolderName, fileLangCode } from '@/lib/uploadFilenames';
import { assetGlobalIdx } from '@/lib/assetNumber';
import { completeVersions, pendingVersions } from '@/lib/versionStatus';
import { formatVersionsHint } from '@/lib/versionsHint';
import type { UploadTarget } from '@/lib/uploadPipeline';

/** UploadSummary — mirrors vanilla `renderUploadSummary` + the "Upload All"
 *  loop (legacy/index.html lines 5435-5760). Iterates every asset that is
 *  ready (TTS: every block has a fav take with content; STS: master timeline
 *  has at least one clip) but not yet uploaded for the *current* state, calls
 *  `submitAssetUpload` for each in sequence, and appends the resulting
 *  version to the store. TTS post-upload cleanup matches TTSView
 *  (legacy 10003-10018): blocks keep their text/params but takes are
 *  stripped so the talent starts the next iteration with empty chips. */
export function UploadSummary() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  const config = useSessionStore((s) => s.config);
  const toolId = useSessionStore((s) => s.toolId);
  const sessionFolder = useSessionStore((s) => s.sessionFolder);
  const shortId = useSessionStore((s) => s.shortId);
  const userName = useSessionStore((s) => s.name);
  const versions = useSessionStore((s) => s.versions);
  const assetBlocks = useSessionStore((s) => s.assetBlocks);
  const stsAssets = useSessionStore((s) => s.stsAssets);
  const customAssetsState = useSessionStore((s) => s.customAssets);
  const freeMode = useSessionStore((s) => s.freeMode);
  const addVersion = useSessionStore((s) => s.addVersion);
  // Same leave-page guard as the generation view — the summary is exactly
  // where a talent closes the tab believing everything is delivered.
  useLeaveGuard();

  // Re-derive the asset list with a stable selector — selectAllAssets builds
  // a fresh array each call, so we feed it explicit slices via useMemo.
  // Predefined free assets get the vanilla voice fallback (legacy line 4014).
  const assets = useMemo(() => {
    const cfg = applyPredefinedFreeVoiceFallback(config?.assets, config?.voices);
    const custom = customAssetsState.map((ca) => ({
      ...ca,
      assetType: 'free' as const,
      _isCustom: true as const,
    }));
    return [...cfg, ...custom];
  }, [config?.assets, config?.voices, customAssetsState]);
  const predefinedCount = config?.assets.length ?? 0;

  const [busy, setBusy] = useState(false);
  // 'all' = the user clicked "Upload All" (mass upload over `uploadable`).
  // 'single' = the user clicked a per-row "Upload" button (one asset only).
  // Drives the footer label + button text so a single-asset upload doesn't
  // disguise itself as a mass upload.
  const [busyKind, setBusyKind] = useState<'all' | 'single' | null>(null);
  // Per-row progress label shown inline in the active row. We deliberately
  // keep this scoped to *one* row at a time — during Upload All the loop
  // moves the label from row to row; during a single upload only that row
  // shows the live label and the global footer/button stay quiet.
  const [progressRowId, setProgressRowId] = useState<string | null>(null);
  const [progressMessage, setProgressMessage] = useState<string>('');
  // assetId -> 'queued' | 'uploading' | 'done' | 'error'
  const [assetStatuses, setAssetStatuses] = useState<
    Record<string, 'queued' | 'uploading' | 'done' | 'error'>
  >({});

  // Compute readiness once per render. Mirrors Generation/index.tsx's
  // status logic so the summary stays in lock-step with the sidebar icons.
  const rows = useMemo(() => {
    return assets.map((asset, index) => {
      // Nur vollständige Versionen zählen als Lieferung (Audit M15); halbe
      // (pending) Versionen werden in der Versionszeile eigens genannt.
      const allVersions = versions[asset.id] ?? [];
      const versionList = completeVersions(allVersions);
      const pendingList = pendingVersions(allVersions);
      const last = versionList[versionList.length - 1];
      const uploaded = Boolean(last);
      const isCustom = index >= predefinedCount;

      let blocksReady: boolean;
      // Free/custom assets in STS mode must be judged by their master
      // timeline, not by (stale) TTS blocks — same dispatch as the sidebar
      // and exportSnapshot (audit high: summary treated them as TTS).
      if (isStsMode(asset.assetType, freeMode[index] as 'tts' | 'sts' | undefined)) {
        blocksReady = (stsAssets[index]?.master?.length ?? 0) > 0;
      } else {
        const bs = assetBlocks[index] ?? [];
        blocksReady =
          bs.length > 0 &&
          bs.every(
            (b) => b.favIdx >= 0 && !!b.takes[b.favIdx] && !!b.text?.trim(),
          );
      }
      // "New work" mirrors Generation/index.tsx — if there's already a version
      // AND no fresh ready blocks, the asset is fully shipped.
      const hasNewWork = blocksReady && uploaded;
      const status: 'uploaded' | 'ready' | 'pending' =
        uploaded && !hasNewWork
          ? 'uploaded'
          : blocksReady
            ? 'ready'
            : 'pending';
      const live = assetStatuses[asset.id];
      return {
        id: asset.id,
        title: asset.title || `Asset ${index + 1}`,
        index,
        asset,
        isCustom,
        blocksReady,
        uploaded,
        last,
        status,
        liveStatus: live,
        // Surface every version so the row can render the
        // "Existing versions: v1, v2, v3" hint (legacy line 5536).
        versionList,
        pendingList,
      };
    });
  }, [
    assets,
    versions,
    assetBlocks,
    stsAssets,
    freeMode,
    predefinedCount,
    assetStatuses,
  ]);

  const uploadable = useMemo(
    () => rows.filter((r) => r.blocksReady),
    [rows],
  );
  const pendingCount = rows.filter((r) => !r.blocksReady && !r.uploaded).length;

  /** Run `submitAssetUpload` for the given row. Builds the same context
   *  TTSView/STSView build for the active asset, except no per-asset
   *  modal prompts — the summary's mass upload doesn't ask for note/video.
   *  Returns true on success, false on failure. Updates assetStatuses
   *  for live progress feedback. Optionally accepts an AbortSignal so the
   *  outer Upload All loop can cancel both the in-flight upload and any
   *  remaining queued rows. */
  const uploadRow = useCallback(
    async (
      row: (typeof rows)[number],
      signal?: AbortSignal,
    ): Promise<boolean> => {
      if (!toolId || !sessionFolder) {
        toast.show('Session not ready for upload', 4000);
        return false;
      }
      setAssetStatuses((s) => ({ ...s, [row.id]: 'uploading' }));
      setProgressRowId(row.id);
      setProgressMessage('Preparing…');
      // Sidebar/pill-bar spinner — mirrors vanilla `_sidebarSpinners.add`
      // (legacy/index.html line 3729). Cleared in the finally below so an
      // error mid-upload still resets the icon. Each row is keyed by
      // `row.index`, so concurrent rows in the loop spin independently.
      useSessionStore.getState().setAssetBusy(row.index, true);
      let uploadTarget: UploadTarget | null = null;
      try {
        // For free/custom assets the actual sub-mode lives in `freeMode[idx]`
        // (assetType stays 'free') — isStsMode folds that in so an STS-mode
        // free asset uploads its master timeline instead of stale TTS blocks.
        const rowFreeMode = freeMode[row.index] as 'tts' | 'sts' | undefined;
        const isSts = isStsMode(row.asset.assetType, rowFreeMode);
        const blocks = isSts
          ? stsAssetToBlocks(stsAssets[row.index])
          : (assetBlocks[row.index] ?? []);
        if (blocks.length === 0) {
          throw new Error('No blocks ready for upload');
        }
        // Cloud-Video-Dauer nachladen, falls das Asset seit dem Reload nie
        // geöffnet war — sonst rendert der Snapshot mit targetDuration/30 s
        // (Audit H6).
        await ensureVideoDurations(toolId, assets, [row.index]);
        const { exportInput, videoSrc: snapshotVideoSrc } = snapshotExportInput(row.index);
        const langCode = fileLangCode(config);
        const assetVoiceId = getPrimaryVoiceId(row.asset);
        const voiceName =
          config?.voices?.find((v) => v.id === assetVoiceId)?.name ??
          'Voice';
        // Cloud-Video: „Upload all" fragt nicht nach, also entscheidet allein
        // die Größe, ob das Premix-MP4 mitgeht (Spec 2026-09-21 § 7). Ein
        // eigenes Video des Talents wird hier ohnehin nie mitgeladen.
        const hasOwnVideo = !!useVideoStore.getState().blobs[row.index];
        const cloudVideoSize =
          row.asset.videoPath && !hasOwnVideo
            ? await videoInputSize(toolId, row.asset.videoPath)
            : 0;
        const versionList = versions[row.asset.id] ?? [];
        const nextVersion =
          versionList.length > 0
            ? Math.max(...versionList.map((v) => v.version)) + 1
            : 1;
        // Tatsächliche Version/Ordner aus der Pipeline — für das Aufräumen
        // nach einem Abbruch (Audit M15).
        uploadTarget = {
          version: nextVersion,
          assetFolderName: buildAssetFolderName({
            assetIdx: assetGlobalIdx(row.asset, row.index),
            title: row.asset.title,
            isCustom: row.isCustom,
          }),
        };
        const result = await submitAssetUpload({
          toolId,
          videoSrc: snapshotVideoSrc,
          sessionFolder,
          langCode,
          shortId,
          userName,
          // Globale Asset-Nummer für Ordner/Dateinamen (Audit 2026-09-24 H4).
          assetIdx: assetGlobalIdx(row.asset, row.index),
          asset: {
            id: row.asset.id,
            videoPath: row.asset.videoPath ?? null,
            title: row.asset.title,
            assetType: row.asset.assetType,
            // Custom assets carry per-mode editable scripts (scriptTts /
            // scriptSts); pick the one matching the row's freeMode and fall
            // back through legacy `script` for silent migration. Predefined
            // assets keep using the admin-set `script` verbatim.
            script: row.isCustom
              ? (
                  isSts
                    ? row.asset.scriptSts ?? row.asset.script ?? ''
                    : row.asset.scriptTts ?? row.asset.script ?? ''
                )
              : row.asset.script ?? '',
            isCustom: row.isCustom,
            // Multi-Voice (#173) — pipeline appends _VoiceN suffix when
            // length ≥ 2; single-voice assets keep their pre-Phase-9
            // filenames byte-identical.
            voiceIds: row.asset.voiceIds ?? [],
          },
          voiceName,
          blocks,
          videoDuration:
            useVideoStore.getState().durations[row.index] ?? 0,
          exportInput,
          freeMode:
            row.asset.assetType === 'free'
              ? (freeMode[row.index] as 'tts' | 'sts' | undefined)
              : undefined,
          nextVersion,
          // Summary mass upload: no per-asset video opt-in, no note (matches
          // vanilla "Upload All" — the talent uses per-asset Upload buttons in
          // TTS/STSView when they want a note or to bundle the video).
          // Ausnahme: ein großes Cloud-Video. Im Modal steht die Checkbox dafür
          // vorbelegt an, und „Upload all" ist die finale Lieferung — ohne
          // dieses Flag fiele das Premix-MP4 hier still weg (Spec 2026-09-21
          // § 7). Eigene Videos bleiben unberührt (kein Blob, kein Flag).
          includeVideo: !shouldAutoUploadCloudVideo(cloudVideoSize),
          videoBlob: null,
          cloudVideoSize,
          note: null,
          isSts,
          // Per-row progress label rendered inline next to the active row's
          // status icon. We flatten the structured pipeline event into a
          // short label and skip `file-bytes` to avoid a per-second
          // re-render storm during big uploads. The label is scoped to
          // `progressRowId` so it never leaks outside the row that's
          // actually uploading right now.
          onProgress: (e) => {
            if (e.kind === 'message') {
              setProgressMessage(e.text);
            } else if (e.kind === 'overall' && e.text) {
              setProgressMessage(e.text);
            } else if (e.kind === 'warning') {
              // Hier gibt es kein Upload-Modal, das den Hinweis am Ende
              // zeigen könnte — also als Toast, damit ein gescheiterter
              // Video-Mux auch bei „Upload all" sichtbar bleibt.
              toast.show(e.text, 6000);
            }
          },
          signal,
          onUploadTarget: (t) => { uploadTarget = t; },
        });
        addVersion(row.asset.id, result);
        // Post-upload cleanup — same Option B (FB3#9) shape as TTSView and
        // STSView. TTS: strip takes per block (preserves text/params).
        // STS: clear the master clips that defined the just-uploaded
        // version (preserves the take pool so the user can iterate).
        if (isSts) {
          useSessionStore.setState((s) => {
            const cur = s.stsAssets[row.index];
            if (!cur || cur.master.length === 0) return {};
            return {
              stsAssets: {
                ...s.stsAssets,
                [row.index]: { ...cur, master: [] },
              },
            };
          });
        } else {
          useSessionStore.setState((s) => {
            const cur = s.assetBlocks[row.index] ?? [];
            const next = cur.map((b) => ({
              ...b,
              takes: [],
              favIdx: -1,
              takeCounter: 0,
              offset: 0,
              pinned: false,
            }));
            const writes: Partial<typeof s> = {
              assetBlocks: { ...s.assetBlocks, [row.index]: next },
            };
            // If the user happens to be sitting on this asset, keep the
            // active `blocks` mirror in sync so a navigation-back doesn't
            // see stale takes.
            if (s.currentAssetIdx === row.index) {
              writes.blocks = next;
            }
            return writes;
          });
        }
        setAssetStatuses((s) => ({ ...s, [row.id]: 'done' }));
        return true;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Upload failed';
        // Distinguish user-cancel from genuine failures so the row gets a
        // neutral "queued" reset instead of a red "error" pill that would
        // suggest something broke. Aborts come as DOMException (fetch,
        // OfflineAudioContext) OR as ApiError mit name 'AbortError' (XHR-
        // Upload) — deshalb nur über den Namen erkennen (Audit M15).
        const isAbort =
          !!err && typeof err === 'object' && (err as { name?: string }).name === 'AbortError';
        if (isAbort) {
          setAssetStatuses((s) => ({ ...s, [row.id]: undefined } as typeof s));
          // Halbe Version wegräumen — mit der Version, unter der die Pipeline
          // tatsächlich lief (idempotent, 404 = schon weg).
          if (uploadTarget) {
            // 409 version_complete → Versionen nachladen (lib/cancelUpload.ts).
            await cancelAbortedUpload(toolId, {
              sessionFolder,
              assetFolderName: uploadTarget.assetFolderName,
              version: uploadTarget.version,
              uploadId: uploadTarget.uploadId,
              assetId: row.asset.id,
            }, 'UploadSummary');
          }
        } else {
          setAssetStatuses((s) => ({ ...s, [row.id]: 'error' }));
          toast.show(`"${row.title}" failed: ${msg}`, 5000);
        }
        return false;
      } finally {
        useSessionStore.getState().setAssetBusy(row.index, false);
      }
    },
    [
      toolId,
      sessionFolder,
      shortId,
      userName,
      config,
      assets,
      versions,
      assetBlocks,
      stsAssets,
      freeMode,
      addVersion,
      toast,
    ],
  );

  const handleUploadOne = useCallback(
    async (rowId: string) => {
      const row = rows.find((r) => r.id === rowId);
      if (!row || !row.blocksReady || busy) return;
      setBusy(true);
      setBusyKind('single');
      try {
        await uploadRow(row);
      } finally {
        setBusy(false);
        setBusyKind(null);
        setProgressRowId(null);
        setProgressMessage('');
      }
    },
    [rows, busy, uploadRow],
  );

  // Holds the in-flight Upload All AbortController so the Cancel button
  // can abort both the active row's pipeline and break out of the queue
  // loop. Ref-only — no re-render needed, the cancel button's visibility
  // is driven by `busyKind === 'all'`.
  const uploadAllAbortRef = useRef<AbortController | null>(null);

  const handleUploadAll = useCallback(async () => {
    if (uploadable.length === 0 || busy) return;
    const controller = new AbortController();
    uploadAllAbortRef.current = controller;
    setBusy(true);
    setBusyKind('all');
    // Pre-mark every queued asset so the row UI reflects the queue.
    setAssetStatuses(
      Object.fromEntries(uploadable.map((r) => [r.id, 'queued'])),
    );
    let successCount = 0;
    let failCount = 0;
    let cancelled = false;
    try {
      for (const row of uploadable) {
        if (controller.signal.aborted) {
          cancelled = true;
          break;
        }
        const ok = await uploadRow(row, controller.signal);
        if (ok) successCount++;
        else if (controller.signal.aborted) {
          cancelled = true;
          break;
        } else {
          failCount++;
        }
      }
    } finally {
      uploadAllAbortRef.current = null;
      setBusy(false);
      setBusyKind(null);
      setProgressRowId(null);
      setProgressMessage('');
      if (cancelled) {
        // Reset any still-queued rows so the next Upload All starts clean.
        setAssetStatuses((prev) => {
          const next = { ...prev };
          for (const r of uploadable) {
            if (next[r.id] === 'queued' || next[r.id] === 'uploading') {
              delete next[r.id];
            }
          }
          return next;
        });
      }
    }
    if (cancelled) {
      toast.show(
        `Upload cancelled${successCount > 0 ? ` — ${successCount} uploaded before cancel` : ''}`,
        4000,
      );
    } else if (failCount === 0) {
      toast.show(
        `Uploaded ${successCount} asset${successCount === 1 ? '' : 's'}`,
        3000,
      );
    } else if (successCount > 0) {
      toast.show(
        `Uploaded ${successCount}, ${failCount} failed`,
        5000,
      );
    }
  }, [uploadable, busy, uploadRow, toast]);

  const handleCancelAll = useCallback(() => {
    const controller = uploadAllAbortRef.current;
    if (!controller) return;
    controller.abort();
  }, []);

  if (!config) return null;

  return (
    <AppShell>
      <main className={styles.wrap}>
        <div className={styles.content}>
          <button
            type="button"
            className={styles.backLink}
            onClick={() => navigate(withSearch(`/${id}/generate`, location.search))}
          >
            <i className="ph-bold ph-arrow-left" aria-hidden="true" />
            Back to project
          </button>

          <h1 className={styles.title}>Upload Summary</h1>

          <div className={styles.list}>
            {rows.map((r) => {
              const liveLabel =
                r.liveStatus === 'uploading'
                  ? 'Uploading…'
                  : r.liveStatus === 'queued'
                    ? 'Queued'
                    : r.liveStatus === 'error'
                      ? 'Failed'
                      : null;
              const showOk = r.status === 'uploaded' || r.liveStatus === 'done';
              // While the row is mid-upload (or queued/failed) the "Ready to
              // upload" / "No content yet" hint is stale — it described the
              // state *before* the user clicked Upload. Suppress meta in that
              // window so the row reads as a single clean phrase
              // ("Uploading…", "Queued", "Failed") instead of the previously
              // doubled-up "Uploading… · Ready to upload" the user spotted.
              const meta = liveLabel
                ? null
                : r.last
                  ? `v${r.last.version} · ${new Date(r.last.uploadedAt).toLocaleString()}`
                  : r.blocksReady
                    ? 'Ready to upload'
                    : 'No content yet';
              const statusText = liveLabel
                ? liveLabel
                : showOk
                  ? 'Uploaded'
                  : r.blocksReady
                    ? 'Ready'
                    : 'Not yet ready';
              const isUploading = liveLabel === 'Uploading…';
              const iconClass = isUploading
                ? `ph-bold ph-spinner ${styles.spin}`
                : liveLabel === 'Failed'
                  ? 'ph-fill ph-warning-circle'
                  : showOk
                    ? 'ph-fill ph-check-circle'
                    : r.blocksReady
                      ? 'ph-fill ph-circle'
                      : 'ph-bold ph-circle-dashed';
              // "Existing versions: v1, v2, v3" hint — mirrors vanilla
              // `renderUploadSummary` (legacy/index.html line 5536). Only
              // rendered when at least one version exists; cleared
              // automatically when the asset is deleted (versions[id] gone).
              const versionsHint = formatVersionsHint(r.versionList, r.pendingList);
              // Inline live progress label — only the row that is currently
              // uploading shows it. Replaces the previous global footer
              // line so single-asset uploads don't surface a misplaced
              // progress strip near the Upload All button.
              const showRowProgress =
                progressRowId === r.id && isUploading && !!progressMessage;
              return (
                <div key={r.id} className={styles.row}>
                  <div className={styles.rowMain}>
                    <div className={styles.rowTitle} {...scriptAttrs(r.title)}>{r.title}</div>
                    <div
                      className={`${styles.rowStatus} ${
                        showOk ? styles.statusOk : styles.statusPending
                      }`}
                    >
                      <i className={iconClass} aria-hidden="true" />
                      <span>
                        {statusText}
                        {meta && (
                          <span className={styles.rowMeta}> · {meta}</span>
                        )}
                      </span>
                    </div>
                    {showRowProgress && (
                      <div className={styles.rowProgress}>
                        {progressMessage}
                      </div>
                    )}
                    {versionsHint && (
                      <div className={styles.rowVersions}>{versionsHint}</div>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    onClick={() => handleUploadOne(r.id)}
                    disabled={
                      busy || !r.blocksReady || r.liveStatus === 'uploading'
                    }
                  >
                    {showOk && r.blocksReady
                      ? 'Re-upload'
                      : showOk
                        ? 'Uploaded'
                        : 'Upload'}
                    {!showOk && <i className="ph-bold ph-cloud-arrow-up" />}
                  </Button>
                </div>
              );
            })}
          </div>

        </div>

        {/* Sticky bottom bar — mirrors BottomBar in TTS/STSView. Pushed to
         *  the viewport bottom on short pages via `margin-top:auto`, sticks
         *  to the scroll-container floor on long pages via
         *  `position:sticky; bottom:0`. Back to project sits left (mirrors
         *  the header link); Upload All sits right. */}
        <div className={styles.bottomBar} data-sticky-bottom-bar="1">
          <div className={styles.bottomBarLeft}>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(withSearch(`/${id}/generate`, location.search))}
              disabled={busy}
            >
              <i className="ph-bold ph-arrow-left" /> Back to project
            </Button>
            <span className={styles.bottomBarMeta}>
              {busyKind === 'all' && progressMessage
                ? progressMessage
                : uploadable.length > 0
                  ? `${uploadable.length} ready to upload`
                  : pendingCount > 0
                    ? `${pendingCount} asset${pendingCount === 1 ? '' : 's'} need content`
                    : 'All assets uploaded'}
            </span>
          </div>
          <div className={styles.bottomBarRight}>
            {busyKind === 'all' && (
              <Button
                size="sm"
                variant="ghost"
                onClick={handleCancelAll}
              >
                <i className="ph-bold ph-x" /> Cancel Upload
              </Button>
            )}
            <Button
              size="sm"
              onClick={handleUploadAll}
              disabled={uploadable.length === 0 || busy}
            >
              {busyKind === 'all' ? 'Uploading…' : 'Upload All'}
              {busyKind !== 'all' && <i className="ph-bold ph-cloud-arrow-up" />}
            </Button>
          </div>
        </div>
      </main>
    </AppShell>
  );
}
